/**
 * Centralized, type-safe environment configuration.
 *
 * Rules:
 *  1. This is the ONLY file that reads import.meta.env — nowhere else.
 *  2. Required vars (marked below) throw at startup if missing in production.
 *  3. Build-time constants (__APP_VERSION__ etc.) are injected by vite.config.ts.
 */

// ── Helpers ────────────────────────────────────────────────────────────────

function get(key: string, fallback: string): string {
  return (import.meta.env[key] as string | undefined)?.trim() || fallback;
}

/**
 * In production/staging, a missing required variable throws immediately so
 * the deployment fails loudly rather than silently using wrong endpoints.
 */
function require(key: string): string {
  const value = (import.meta.env[key] as string | undefined)?.trim();
  if (!value && (import.meta.env.MODE === 'production' || import.meta.env.MODE === 'staging')) {
    throw new Error(
      `[env] Required variable "${key}" is missing. ` +
      `Set it in .env.${import.meta.env.MODE} before building.`,
    );
  }
  return value ?? '';
}

// ── Build-time defines (injected via vite.config.ts define option) ──────────
//
// Vite replaces these identifiers with their literal values at build time.
// The `typeof` guard prevents ReferenceError in environments where the
// define was not applied (e.g. unit tests, SSR runners).

declare const __APP_VERSION__: string;
declare const __BUILD_MODE__:  string;
declare const __BUILD_TIME__:  string;

const _APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
const _BUILD_MODE  = typeof __BUILD_MODE__  !== 'undefined' ? __BUILD_MODE__  : 'development';
const _BUILD_TIME  = typeof __BUILD_TIME__  !== 'undefined' ? __BUILD_TIME__  : '';

// ── Config object ────────────────────────────────────────────────────────────

export const env = {
  // ── API ────────────────────────────────────────────────────────────────────
  /**
   * Full API origin (e.g. https://api.your-domain.com).
   * Required in production — throws if missing.
   */
  API_BASE_URL: import.meta.env.MODE === 'development'
    ? get('VITE_API_BASE_URL', 'http://localhost:3000')
    : require('VITE_API_BASE_URL'),

  API_PREFIX: get('VITE_API_PREFIX', '/api/v1'),

  /** Axios request timeout in ms */
  API_TIMEOUT: Number(get('VITE_API_TIMEOUT', '30000')),

  // ── App ────────────────────────────────────────────────────────────────────
  APP_NAME: get('VITE_APP_NAME', 'AI Copilot'),
  APP_ENV:  get('VITE_APP_ENV', 'development') as 'development' | 'staging' | 'production',

  // ── Build metadata ─────────────────────────────────────────────────────────
  APP_VERSION: _APP_VERSION,
  BUILD_MODE:  _BUILD_MODE,
  BUILD_TIME:  _BUILD_TIME,

  // ── Widget ─────────────────────────────────────────────────────────────────
  WIDGET_DEFAULT_POSITION: get(
    'VITE_WIDGET_DEFAULT_POSITION',
    'bottom-right',
  ) as 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left',

  /** Public base URL for the widget script (defaults to same origin) */
  WIDGET_BASE_URL: get('VITE_WIDGET_BASE_URL', ''),

  // ── Feature flags ──────────────────────────────────────────────────────────
  ENABLE_DEVTOOLS:   get('VITE_ENABLE_DEVTOOLS', 'false') === 'true',
  SHOW_CHAT_PREVIEW: get('VITE_SHOW_CHAT_PREVIEW', 'true') === 'true',

  // ── Computed ───────────────────────────────────────────────────────────────

  get isDev():     boolean { return this.APP_ENV === 'development'; },
  get isStaging(): boolean { return this.APP_ENV === 'staging'; },
  get isProd():    boolean { return this.APP_ENV === 'production'; },

  /** Full API base including prefix (e.g. https://api.example.com/api/v1) */
  get apiRoot(): string {
    return `${this.API_BASE_URL}${this.API_PREFIX}`;
  },
} as const;

export type Env = typeof env;
